import { Box } from "@mantine/core";
import type { DatesRangeValue } from "@mantine/dates";
import { MonthPickerInput } from "@mantine/dates";
import { IconFileX, IconReportSearch } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect, useMemo, useState } from "react";
import { DeleteConfirmationModal } from "../../../../components/BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { LoadOrError } from "../../../../components/LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../../../components/StaffViewToggle/useShouldShowStaffView";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getDateEndColumn } from "../../../../mantine/mrt/components/columns/getDateEndColumn";
import { getDateStartColumn } from "../../../../mantine/mrt/components/columns/getDateStartColumn";
import { Paragraph6EEGCreditResponseState } from "../../../../types/api.types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { useParagraph6Cancellations } from "../../hooks/useParagraph6Cancellations";
import { useParagraph6CreditMutations } from "../../hooks/useParagraph6CreditMutations";
import { useParagraph6Credits } from "../../hooks/useParagraph6Credits";
import { FINANCIAL_DOCUMENT_TYPES } from "../../Paragraph6.constants";
import {
  FinancialDocumentType,
  type Paragraph6FinancialDocument
} from "../../Paragraph6.types";
import { Paragraph6ConfirmCancellationModal } from "../Paragraph6ConfirmCancellationModal/Paragraph6ConfirmCancellationModal";
import { Paragraph6ParticipationModal } from "../Paragraph6ParticipationModal/Paragraph6ParticipationModal";
import { getCreationDateColumn } from "./Columns/getCreationDateColumn";
import { getCreditNumberColumn } from "./Columns/getCreditNumberColumn";
import { getDocumentColumn } from "./Columns/getDocumentColumn";
import { getPayDateColumn } from "./Columns/getPayDateColumn";
import { getStateColumn } from "./Columns/getStateColumn";
import "./Paragraph6CreditsTable.scss";

interface Paragraph6CreditsTableProps {
  variantId: number;
  isPaymentInformationView?: boolean;
}

function Paragraph6CreditsTable({
  variantId,
  isPaymentInformationView
}: Paragraph6CreditsTableProps) {
  const {
    credits = [],
    isLoading: paragraph6CreditsLoading,
    error: paragraph6CreditsError
  } = useParagraph6Credits(variantId);
  const { cancellations = [], isLoading: cancellationsLoading } =
    useParagraph6Cancellations(variantId);
  const { shouldShowStaffView } = useShouldShowStaffView();
  const [cancellationCreditId, setCancellationCreditId] = useState<
    string | undefined
  >(undefined);
  const handleCloseParticipationModal = () => {
    setParticipationCredit(null);
  };

  const tableData = useMemo(() => {
    if (paragraph6CreditsLoading || cancellationsLoading) {
      return [];
    }
    const items = isPaymentInformationView
      ? credits
      : [...credits, ...cancellations];

    return items.filter((credit) => {
      return isPaymentInformationView
        ? credit.is_payment_information
        : !credit.is_payment_information;
    });
  }, [
    credits,
    cancellations,
    isPaymentInformationView,
    paragraph6CreditsLoading,
    cancellationsLoading
  ]);

  const [creditToDelete, setCreditToDelete] =
    useState<Paragraph6FinancialDocument | null>(null);
  const [participationCredit, setParticipationCredit] =
    useState<Paragraph6FinancialDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown | null>(null);

  const [selectedDateRange, setSelectedDateRange] = useState<
    [DateTime, DateTime | null]
  >([DateTime.now().startOf("year"), DateTime.now().endOf("month")]);

  const [rangeFromData, setRangeFromData] = useState<
    [DateTime, DateTime] | null
  >(null);

  const columns = useMemo<MRT_ColumnDef<Paragraph6FinancialDocument>[]>(
    () => [
      getDateStartColumn<Paragraph6FinancialDocument>({
        key: "billing_period_start",
        options: {
          header: "Leistungsbeginn"
        }
      }),
      getDateEndColumn<Paragraph6FinancialDocument>({
        key: "billing_period_end",
        options: {
          header: "Leistungsende"
        }
      }),
      {
        header: "Typ",
        id: "type",
        accessorFn: (rowData) =>
          rowData.type ? FINANCIAL_DOCUMENT_TYPES[rowData.type] : "",
        size: 95,
        enableResizing: false,
        filterVariant: "multi-select"
      },
      getDocumentColumn(),
      {
        header: "Gesellschaft",
        accessorKey: "supplier",
        enableCellHoverReveal: true,
        filterVariant: "multi-select",
        size: 165
      },
      {
        header: "Gemeinde / Kommune",
        accessorKey: "community_name",
        enableCellHoverReveal: true,
        filterVariant: "multi-select"
      },
      getCreditNumberColumn(),
      getStateColumn(variantId),
      getCreationDateColumn(),
      getPayDateColumn()
    ],
    [variantId]
  );

  const { paragraph6CreditDeleteMutation } =
    useParagraph6CreditMutations(variantId);

  function handleClickDeleteCredit(credit: Paragraph6FinancialDocument) {
    setCreditToDelete(credit);
  }

  function handleUserAcceptsDeletion() {
    if (creditToDelete) {
      setIsDeleting(true);
      paragraph6CreditDeleteMutation.mutate(creditToDelete.id, {
        onError: (error) => {
          setDeleteError(error);
        },
        onSettled: () => {
          setIsDeleting(false);
          setCreditToDelete(null);
        }
      });
    }
  }

  function documentTypeToDelete() {
    if (!creditToDelete || !creditToDelete.type) return "";
    return (
      FINANCIAL_DOCUMENT_TYPES[creditToDelete.type] +
      " für " +
      creditToDelete.community_name
    );
  }

  useEffect(() => {
    const range = getDateRangeForTableData(tableData);
    if (!range) {
      return;
    }

    setRangeFromData(range);
    setSelectedDateRange(range);
  }, [tableData]);

  const dataBySelectedDateRange = useMemo(() => {
    return filterTableDataByDateRange(tableData, selectedDateRange);
  }, [tableData, selectedDateRange]);

  const table = useMantineReactTable<Paragraph6FinancialDocument>({
    ...getDefaultMRTOptions<Paragraph6FinancialDocument>({
      emptyRowsFallbackText: "Keine Ergebnisse zum Anzeigen gefunden",
      hideBorder: true,
      initialState: {
        showGlobalFilter: true
      },
      searchTextInput: {
        placeholder:
          "Suche nach Typ, Dokument, Gesellschaft, Gemeinde / Kommune ..."
      },
      "aria-label": "Gutschriften"
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        other: [
          {
            text: "Beteiligungsübersicht",
            icon: IconReportSearch,
            color: "secondary",
            enableAction: (cell) =>
              cell.row.original.type !== FinancialDocumentType.Cancellation &&
              cell.row.original.state !==
                Paragraph6EEGCreditResponseState.cancelled,
            onClick: (row) => setParticipationCredit(row.original)
          },
          {
            text: "Stornieren",
            icon: IconFileX,
            color: "error",
            enableAction: (cell) =>
              cell.row.original.type === FinancialDocumentType.Credit &&
              cell.row.original.state !==
                Paragraph6EEGCreditResponseState.cancelled,
            onClick: (row) => setCancellationCreditId(row.original.id)
          }
        ],
        delete: shouldShowStaffView
          ? {
              text: "Löschen",
              enableDelete: (cell) =>
                cell.row.original.type === FinancialDocumentType.Credit &&
                cell.row.original.state !==
                  Paragraph6EEGCreditResponseState.cancelled,
              onClick: (row) => handleClickDeleteCredit(row.original)
            }
          : undefined
      },
      displayColumnDefOptions: {
        "mrt-row-actions": {
          size: 100
        }
      }
    }),
    renderTopToolbarCustomActions: () => {
      return (
        <Box w="100%">
          <MonthPickerInput
            allowSingleDateInRange
            className="date-range-input"
            clearable={
              rangeFromData
                ? selectedDateRange[0] !== rangeFromData[0] ||
                  selectedDateRange[1] !== rangeFromData[1]
                : false
            }
            clearButtonProps={{
              onClick: () =>
                rangeFromData ? setSelectedDateRange(rangeFromData) : null
            }}
            display={"flex"}
            mx="sm"
            style={{
              alignItems: "flex-start"
            }}
            type="range"
            value={[
              selectedDateRange[0].toJSDate(),
              selectedDateRange[1] ? selectedDateRange[1].toJSDate() : null
            ]}
            onChange={handleDatePickerChange}
          />
        </Box>
      );
    },
    layoutMode: "grid",
    enableColumnResizing: true,
    enablePagination: dataBySelectedDateRange.length > 5,
    enableColumnFilters: true,
    columnFilterDisplayMode: "popover",
    enableFacetedValues: true,
    enableTopToolbar: true,
    columns,
    data: dataBySelectedDateRange
  });

  function handleDatePickerChange(dateRange: DatesRangeValue<string>) {
    // only react if a valid & complete range is set
    if (dateRange[0] && (dateRange[1] || dateRange[1] === null)) {
      const firstDate = DateTime.fromISO(dateRange[0]);
      const lastDate = dateRange[1]
        ? DateTime.fromISO(dateRange[1]).endOf("month")
        : null;
      if (firstDate.isValid && (lastDate === null || lastDate.isValid)) {
        setSelectedDateRange([firstDate, lastDate]);
      }
    }
  }

  return (
    <LoadOrError
      error={paragraph6CreditsError}
      loading={paragraph6CreditsLoading || cancellationsLoading}
      loadingMessage={`${isPaymentInformationView ? "Zahlungsinformationen" : "Gutschriften"} werden geladen ...`}
    >
      <div className="Paragraph6CreditsTable">
        <MantineReactTable table={table} />
        <Paragraph6ConfirmCancellationModal
          creditId={cancellationCreditId}
          variantId={variantId}
          onClose={() => setCancellationCreditId(undefined)}
        />
        <DeleteConfirmationModal
          error={deleteError}
          isLoading={isDeleting}
          name={documentTypeToDelete()}
          opened={!!creditToDelete}
          toggleModal={() => setCreditToDelete(null)}
          onConfirm={handleUserAcceptsDeletion}
        />
        {participationCredit && (
          <Paragraph6ParticipationModal
            isOpen={!!participationCredit}
            participationCredit={participationCredit}
            onClose={handleCloseParticipationModal}
          />
        )}
      </div>
    </LoadOrError>
  );
}

function getDateRangeForTableData(
  tableData: Paragraph6FinancialDocument[]
): [DateTime, DateTime] | null {
  if (!tableData.length) {
    return null;
  }

  const starts: DateTime[] = [];
  const ends: DateTime[] = [];

  tableData.forEach((credit) => {
    const start = backendDateOrDateTimeToLuxonDateTime(
      credit.billing_period_start
    );
    const end = backendDateOrDateTimeToLuxonDateTime(credit.billing_period_end);

    if (start.isValid) {
      starts.push(start);
    }
    if (end.isValid) {
      ends.push(end);
    }
  });

  if (!starts.length || !ends.length) {
    return null;
  }

  const oldestStart = starts.reduce((min, current) =>
    current < min ? current : min
  );
  const newestEnd = ends.reduce((max, current) =>
    current > max ? current : max
  );

  return [oldestStart.startOf("month"), newestEnd.endOf("month")];
}

function filterTableDataByDateRange(
  tableData: Paragraph6FinancialDocument[],
  selectedDateRange: [DateTime, DateTime | null]
) {
  const [rangeStart, rangeEnd] = selectedDateRange;

  return tableData.filter((credit) => {
    const billingPeriodStart = backendDateOrDateTimeToLuxonDateTime(
      credit.billing_period_start
    );
    const billingPeriodEnd = backendDateOrDateTimeToLuxonDateTime(
      credit.billing_period_end
    );

    // Include credits where either start or end of the billing period
    // lies within the selected range
    const isStartWithinRange =
      billingPeriodStart >= rangeStart &&
      (rangeEnd === null || billingPeriodStart <= rangeEnd);

    const isEndWithinRange =
      billingPeriodEnd >= rangeStart &&
      (rangeEnd === null || billingPeriodEnd <= rangeEnd);

    return isStartWithinRange || isEndWithinRange;
  });
}

export { Paragraph6CreditsTable, Paragraph6CreditsTableProps };
